import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import { Delete, Edit } from "@mui/icons-material";
import { match as MatchProps } from "react-router";
import {
  Box,
  Button,
  ClickAwayListener,
  IconButton,
  Paper,
  Popper,
  Typography
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

import moment from "moment";
import Panel from "../../../components/Panel";
import { getFullName } from "../../../helpers/formatters";
import OkhatiDialog from "../../../components/Dialog/Dialog";
import { notificationAdd } from "../../../actions/notification";
import { convertADtoBS } from "../../../components/Calendar/functions/calendarFunctions";
import {
  getDentalLabOrder,
  deleteDentalLabOrder,
  patchDentalLabOrder
} from "../../../api/dentalLabOrder";
import HandleDentalLabOrderStatus from "./HandleDentalLabOrderStatus";
import { DentalLabOrderStatus } from "../../../enum/DentalLabOrder";
import { DentalLabOrder } from "../../../interfaces/DentalLabOrder";
import OpdRequestStatusChip from "../OpdRequest/OpdRequestStatusChip";
import { DentalLabOrderPrint } from "./Print/DentalLabOrderPrint";
import CalendarDropdown from "../../../components/CalendarDropdown/CalendarDropdown";

interface DentalLabOrderInfoProps {
  selectedRow?: Partial<DentalLabOrder> | null;
  match: MatchProps<{ id: string }>;
  onClose?: () => void;
  setDentalLabOrderData: React.Dispatch<
    React.SetStateAction<{ results: DentalLabOrder[]; total: number } | null>
  >;
}

const DentalLabOrderInfo = (props: DentalLabOrderInfoProps): React.JSX.Element => {
  const { selectedRow, match, onClose, setDentalLabOrderData } = props;

  const dispatch = useDispatch();
  const dentalLabOrderId = Number(match?.params?.id || selectedRow?.id);

  const [dialogToggle, setDialogToggle] = useState(false);
  const [trialDatesAnchorEl, setTrialDatesAnchorEl] = useState<null | HTMLElement>(null);
  const [editableTrialDates, setEditableTrialDates] = useState<string[]>([]);
  const [dentalLabOrder, setDentalLabOrder] = useState<DentalLabOrder | null>(
    (selectedRow as DentalLabOrder) || null
  );

  const deleteDentalLabOrderFn = async () => {
    try {
      if (!dentalLabOrderId) {
        dispatch(
          notificationAdd({
            id: new Date().getUTCMilliseconds(),
            variant: "error",
            message: "Failed to delete dental lab order",
            autoTimeout: true
          })
        );
        return;
      }

      await deleteDentalLabOrder(dentalLabOrderId);
      setDentalLabOrderData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          total: prev.total === 0 ? 0 : prev.total - 1,
          results: prev.results.filter((item) => item.id !== dentalLabOrderId)
        };
      });
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "success",
          message: "Dental lab order deleted successfully",
          autoTimeout: true
        })
      );
      if (onClose) {
        onClose();
      } else {
        dispatch(push("/assessment/dentalLabOrders"));
      }
    } catch (err) {
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: (err as Error).message || "Failed to delete",
          autoTimeout: true
        })
      );
    }
  };

  const handleSaveTrialDate = async () => {
    try {
      const { data: updatedDentalLabOrder } = await patchDentalLabOrder(
        dentalLabOrderId.toString(),
        {
          ...selectedRow,
          trialDates: editableTrialDates
        }
      );
      setDentalLabOrder(updatedDentalLabOrder);
      setTrialDatesAnchorEl(null);
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "success",
          message: "Trial dates updated successfully",
          autoTimeout: true
        })
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message:
            err?.message ||
            err?.data?.message ||
            "Something went wrong trying to update trial dates.",
          autoTimeout: true
        })
      );
    }
  };

  // effects
  useEffect(() => {
    (async () => {
      try {
        if (!dentalLabOrderId) return;
        const { data } = await getDentalLabOrder(dentalLabOrderId);
        setDentalLabOrder(data || null);
      } catch (err) {
        dispatch(
          notificationAdd({
            id: new Date().getUTCMilliseconds(),
            variant: "error",
            message: (err as Error).message || "Failed to fetch dental lab order",
            autoTimeout: true
          })
        );
      }
    })();
  }, [selectedRow, dispatch, dentalLabOrderId]);

  const titleJsx = (
    <Box
      sx={{ width: "100%" }}
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      pb={1}
    >
      <Box display="flex" sx={{ alignItems: "center" }}>
        <Typography>
          <Box component="span" fontWeight="600" fontSize="16px" pr={2}>
            Dental Lab Order Information
          </Box>
        </Typography>
        <Box pr={2}>
          {dentalLabOrder?.status && <OpdRequestStatusChip status={dentalLabOrder.status} />}
        </Box>
      </Box>
      <Box display="flex">
        {dentalLabOrder && <DentalLabOrderPrint dentalLabOrder={dentalLabOrder} />}

        <Box sx={{ display: "flex", alignItems: "baseline" }}>
          <IconButton
            sx={{ cursor: "pointer", padding: "5px" }}
            onClick={() => setDialogToggle(!dialogToggle)}
          >
            <Delete />
          </IconButton>
        </Box>

        {dentalLabOrder?.status === DentalLabOrderStatus.ORDERED && match && (
          <Box sx={{ display: "flex", alignItems: "baseline" }} ml="8px">
            <IconButton
              sx={{ cursor: "pointer", padding: "5px" }}
              onClick={() => dispatch(push(`${match.url}/edit`))}
            >
              <Edit />
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Panel
      onClose={() => {
        if (onClose) {
          onClose();
        } else {
          dispatch(push("/assessment/dentalLabOrders"));
        }
      }}
      titleJsx={titleJsx}
    >
      {dialogToggle && (
        <OkhatiDialog
          title="Delete Dental Lab Order"
          description="Are you sure you want to delete this dental lab order?"
          next={deleteDentalLabOrderFn}
          cancel={() => setDialogToggle(!dialogToggle)}
          readMode={false}
        />
      )}
      <Box display="flex" flexDirection="column" gap="5px">
        <Box sx={{ display: "flex", alignItems: "baseline" }}>
          <Typography sx={{ width: "30%", flexShrink: 0 }} fontWeight={600}>
            Client
          </Typography>
          <Typography>{dentalLabOrder ? getFullName(dentalLabOrder.client) : ""}</Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "baseline" }}>
          <Typography sx={{ width: "30%", flexShrink: 0 }} fontWeight={600}>
            Teeth Numbers
          </Typography>
          <Typography>
            {(dentalLabOrder?.teeth ?? dentalLabOrder?.teeth ?? []).join(", ") || "-"}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "baseline" }}>
          <Typography sx={{ width: "30%", flexShrink: 0 }} fontWeight={600}>
            Restoration Types
          </Typography>
          <Typography>
            {(dentalLabOrder?.items ?? dentalLabOrder?.items ?? []).join(", ") || "-"}
          </Typography>
        </Box>

        {dentalLabOrder?.shadeDetails && dentalLabOrder.shadeDetails.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "baseline" }}>
            <Typography sx={{ width: "30%", flexShrink: 0 }} fontWeight={600}>
              Shade Details
            </Typography>
            <Typography>{dentalLabOrder.shadeDetails.join(", ")}</Typography>
          </Box>
        )}

        {dentalLabOrder?.occlusalStains && dentalLabOrder.occlusalStains.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "baseline" }}>
            <Typography sx={{ width: "30%", flexShrink: 0 }} fontWeight={600}>
              Occlusal Stains
            </Typography>
            <Typography>{dentalLabOrder.occlusalStains.join(", ")}</Typography>
          </Box>
        )}

        {dentalLabOrder?.ponticDesigns && dentalLabOrder.ponticDesigns.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "baseline" }}>
            <Typography sx={{ width: "30%", flexShrink: 0 }} fontWeight={600}>
              Pontic Designs
            </Typography>
            <Typography>{dentalLabOrder.ponticDesigns.join(", ")}</Typography>
          </Box>
        )}

        {dentalLabOrder?.orderDate && (
          <Box sx={{ display: "flex", alignItems: "baseline" }}>
            <Typography sx={{ width: "30%", flexShrink: 0 }} fontWeight={600}>
              Order Date
            </Typography>

            <Box>
              <Typography>
                {convertADtoBS(new Date(dentalLabOrder.orderDate)).formatted4} BS
              </Typography>
              <Typography sx={{ textTransform: "uppercase" }}>
                {moment(dentalLabOrder.orderDate).format("YYYY-MM-DD hh:mm a")}
              </Typography>
            </Box>
          </Box>
        )}

        {dentalLabOrder?.estimatedDeliveryDate && (
          <Box sx={{ display: "flex", alignItems: "baseline" }}>
            <Typography sx={{ width: "30%", flexShrink: 0 }} fontWeight={600}>
              Delivery Date
            </Typography>
            <Typography>
              {convertADtoBS(new Date(dentalLabOrder.estimatedDeliveryDate)).formatted4} BS
            </Typography>
          </Box>
        )}

        {dentalLabOrder?.deliveryDate && (
          <Box sx={{ display: "flex", alignItems: "baseline" }}>
            <Typography sx={{ width: "30%", flexShrink: 0 }} fontWeight={600}>
              Delivery Date
            </Typography>
            <Box>
              <Typography>
                {convertADtoBS(new Date(dentalLabOrder.deliveryDate)).formatted4} BS
              </Typography>
              <Typography sx={{ textTransform: "uppercase" }}>
                {moment(dentalLabOrder.deliveryDate).format("YYYY-MM-DD hh:mm a")}
              </Typography>
            </Box>
          </Box>
        )}

        {(dentalLabOrder?.note ?? dentalLabOrder?.note) && (
          <Box sx={{ display: "flex", alignItems: "baseline" }}>
            <Typography sx={{ width: "30%", flexShrink: 0 }} fontWeight={600}>
              Note
            </Typography>
            <Typography sx={{ whiteSpace: "pre-wrap" }}>
              {dentalLabOrder?.note ?? dentalLabOrder?.note}
            </Typography>
          </Box>
        )}

        {dentalLabOrder && dentalLabOrder?.status === DentalLabOrderStatus.ORDERED && (
          <Box sx={{ display: "flex", alignItems: "baseline" }}>
            <Typography sx={{ width: "30%", flexShrink: 0 }} fontWeight={600}>
              Status
            </Typography>
            <HandleDentalLabOrderStatus
              dentalLabOrder={dentalLabOrder}
              setDentalLabOrder={setDentalLabOrder}
              setDentalLabOrderData={setDentalLabOrderData}
            />
          </Box>
        )}

        {/* trial dates */}
        <Box display="flex" alignItems="flex-start">
          <Typography sx={{ width: "30%", flexShrink: 0 }} fontWeight={600}>
            Trial Dates
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {/* Display existing trial dates */}
            {dentalLabOrder?.trialDates && dentalLabOrder.trialDates.length > 0 ? (
              dentalLabOrder.trialDates.map((date) => (
                <Typography key={date}>{convertADtoBS(new Date(date)).formatted}</Typography>
              ))
            ) : (
              <Typography color="text.secondary">No trial dates set</Typography>
            )}

            {/* Edit Trial Dates Button */}
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => {
                setEditableTrialDates([...(dentalLabOrder?.trialDates ?? [])]);
                setTrialDatesAnchorEl(trialDatesAnchorEl ? null : e.currentTarget);
              }}
            >
              Edit Trial Dates
            </Button>

            {/* Popper for editing trial dates */}
            <Popper
              id={trialDatesAnchorEl ? "trialDatesPopper" : undefined}
              open={Boolean(trialDatesAnchorEl)}
              anchorEl={trialDatesAnchorEl}
              placement="bottom-start"
              sx={{ zIndex: 1300 }}
              modifiers={[
                {
                  name: "offset",
                  options: {
                    offset: [0, 8]
                  }
                }
              ]}
            >
              <ClickAwayListener onClickAway={() => setTrialDatesAnchorEl(null)}>
                <Paper>
                  <Box
                    sx={(theme) => ({
                      p: 2,
                      border: "1px solid",
                      borderColor: theme.palette.divider,
                      minWidth: "400px",
                      maxWidth: "500px"
                    })}
                  >
                    <Typography variant="subtitle2" fontWeight={600} mb={2}>
                      Manage Trial Dates
                    </Typography>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      {/* Editable trial dates */}
                      {editableTrialDates.map((date, idx) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <CalendarDropdown
                            fullwidth
                            TextFieldProps={{ variant: "outlined", size: "small" }}
                            label={`Trial Date ${idx + 1}`}
                            placeholder="Select trial date"
                            date={date ? new Date(date) : new Date()}
                            onChange={(val) => {
                              if (val) {
                                setEditableTrialDates((prev) => {
                                  const copy = [...prev];
                                  copy[idx] = val.toISOString();
                                  return copy;
                                });
                              }
                            }}
                          />

                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setEditableTrialDates((prev) => prev.filter((_, i) => i !== idx));
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}

                      {/* Add new trial date button */}
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          setEditableTrialDates((prev) => [...prev, new Date().toISOString()])
                        }
                      >
                        Add Trial Date
                      </Button>

                      {/* Action buttons */}
                      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setTrialDatesAnchorEl(null)}
                        >
                          Cancel
                        </Button>
                        <Button size="small" variant="contained" onClick={handleSaveTrialDate}>
                          Save
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </ClickAwayListener>
            </Popper>
          </Box>
        </Box>
      </Box>
    </Panel>
  );
};

export default DentalLabOrderInfo;
